import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setRecipeBuilderAsync } from 'js/actions';
import { useFeatureFlag } from 'new/hooks/useFeatureFlags';
import {
  RECIPE_GROUP_TRANSFORMS_AGGREGATE,
  RECIPE_GROUP_TRANSFORMS_CODE,
} from 'js/constants/schemaEditor';
import { CodeLanguages } from 'js/Constants';
import { getTransformEntities } from 'js/reducers/entities/selectors';
import { ReusableTransformType } from '@nexla/@types';

export type RecipeGroup = { type: string; data?: unknown };

type RecipeCodeGroupData = { type?: string; transformId?: number; codeLanguage?: string };

export type TransformEntities = Partial<Record<number, Pick<ReusableTransformType, 'code_type'>>>;

const getRecipeGroups = (state: any) => state.recipeBuilder.recipeGroups;
const getIsAsync = (state: any) => state.recipeBuilder.isAsync;

/**
 * Reusable transforms carry only a transformId, so the language comes from the code container.
 * Returns undefined when that code container is not loaded, since the language is unknown.
 */
export const isPython3CodeRecipeGroup = (
  tx: RecipeGroup,
  transforms: TransformEntities | undefined,
): boolean | undefined => {
  if (tx.type !== RECIPE_GROUP_TRANSFORMS_CODE) {
    return false;
  }
  const data = tx.data as RecipeCodeGroupData | undefined;
  if (!data) {
    return false;
  }
  if (data.type === 'reusable_tx') {
    const codeType =
      data.transformId === undefined ? undefined : transforms?.[data.transformId]?.code_type;
    return codeType == null ? undefined : codeType === CodeLanguages.PYTHON3;
  }
  return data.codeLanguage === CodeLanguages.PYTHON3;
};

export const useAsyncTransformControl = () => {
  const dispatch = useDispatch();
  const isAsync = useSelector(getIsAsync);
  const recipeGroups = useSelector(getRecipeGroups);
  const transforms: TransformEntities | undefined = useSelector(getTransformEntities);
  const isPython3Enabled = useFeatureFlag('UI_ENABLE_PYTHON3');

  const toggleAsync = useCallback((_: any, checked: boolean) => {
    dispatch(setRecipeBuilderAsync(checked));
  }, []);

  const hasAggregateTransforms = useMemo(() => {
    if (Array.isArray(recipeGroups)) {
      return recipeGroups.some((tx: any) => tx.type === RECIPE_GROUP_TRANSFORMS_AGGREGATE);
    }
    return false;
  }, [recipeGroups]);

  /**
   * undefined when no Python 3 code is found but a reusable transform's language is still unknown
   */
  const isPython3TransformCodeSelected = useMemo(() => {
    if (!Array.isArray(recipeGroups)) {
      return false;
    }
    const results = recipeGroups.map((tx: RecipeGroup) => isPython3CodeRecipeGroup(tx, transforms));
    if (results.includes(true)) {
      return true;
    }
    return results.includes(undefined) ? undefined : false;
  }, [recipeGroups, transforms]);
  const isPython3Selected = isPython3TransformCodeSelected === true;

  useEffect(() => {
    if (hasAggregateTransforms) {
      dispatch(setRecipeBuilderAsync(false));
    }
  }, [hasAggregateTransforms]);

  useEffect(() => {
    /**
     * If Python 3 is enabled, set the async value to true if python 3 transform code is selected, otherwise set it to false.
     * While the language is unknown, keep the current value (in edit mode, the saved node.tag).
     * Depends on isPython3Selected, not the tri-state value, so a reusable transform resolving from
     * unknown to non-Python 3 does not overwrite the saved or user-chosen async value.
     */
    if (isPython3Enabled && isPython3TransformCodeSelected !== undefined) {
      dispatch(setRecipeBuilderAsync(isPython3Selected));
    }
  }, [isPython3Enabled, isPython3Selected]);

  return {
    isAsync,
    toggleAsync,
    /**
     * disableAsyncMode when:
     * - If Python 3 transform code is selected, the async value must be true
     * - If there are any aggregate transforms in the recipe, the async value must be false
     */
    disableAsyncMode: hasAggregateTransforms || isPython3Selected,
  };
};
